export function fmtTs(ts: number | null | undefined): string {
  if (!ts) return "—";
  return new Date(ts * 1000).toLocaleString();
}

export function fmtDate(ts: number | null | undefined): string {
  if (!ts) return "—";
  return new Date(ts * 1000).toLocaleDateString();
}

export function timeAgo(ts: number | null | undefined): string {
  if (!ts) return "—";
  const s = Math.max(0, Math.floor(Date.now() / 1000 - ts));
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  return `${Math.floor(s / 86400)}d`;
}

/** Time left until `ts` (epoch s) as "m:ss" / "h:mm:ss"; "0:00" once past. */
export function timeLeft(ts: number, now: number = Date.now() / 1000): string {
  const s = Math.max(0, Math.floor(ts - now));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = String(s % 60).padStart(2, "0");
  return h > 0 ? `${h}:${String(m).padStart(2, "0")}:${sec}` : `${m}:${sec}`;
}

export function fmtBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

export function fmtEur(cents: number): string {
  return `€${(cents / 100).toFixed(2)}`;
}

// ---------------------------------------------------------------------------
// Calendar dates in a given IANA time zone. A site's "today" and "this month"
// are the SITE's — `toISOString()` would give the UTC date, which is the
// previous/next day for part of every day east/west of Greenwich.
// ---------------------------------------------------------------------------

/** True when `tz` is an IANA zone this browser can format in. */
export function isValidTimeZone(tz: string | null | undefined): boolean {
  if (!tz) return false;
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: tz });
    return true;
  } catch {
    return false;
  }
}

/** The browser's own zone (the default for a new site). */
export function browserTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
}

/** Every IANA zone the browser knows, for the site time-zone picker. */
export function timeZoneList(): string[] {
  const intl = Intl as unknown as { supportedValuesOf?: (key: string) => string[] };
  try {
    const zones = intl.supportedValuesOf?.("timeZone");
    if (zones && zones.length) return zones.includes("UTC") ? zones : ["UTC", ...zones];
  } catch {
    /* older engines */
  }
  return ["UTC", "Europe/Ljubljana", "Europe/Vienna", "Europe/Zagreb", "Europe/Zurich", "Europe/Berlin"];
}

/** {year, month, day} of `date` on the wall clock of `tz` (browser-local when
 *  omitted or invalid). */
export function datePartsIn(date: Date, tz?: string | null): { year: number; month: number; day: number } {
  if (tz && isValidTimeZone(tz)) {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: tz,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).formatToParts(date);
    const get = (type: string) => Number(parts.find((p) => p.type === type)?.value);
    return { year: get("year"), month: get("month"), day: get("day") };
  }
  return { year: date.getFullYear(), month: date.getMonth() + 1, day: date.getDate() };
}

const pad2 = (n: number) => String(n).padStart(2, "0");

/** "YYYY-MM-DD" for `date` (default now) in `tz` (default browser-local). */
export function isoDateIn(tz?: string | null, date: Date = new Date()): string {
  const { year, month, day } = datePartsIn(date, tz);
  return `${year}-${pad2(month)}-${pad2(day)}`;
}

/** "YYYY-MM" of `date` (default now) in `tz` (default browser-local — never
 *  the UTC month, which is wrong for the first/last hours of a month). */
export function currentMonth(tz?: string | null, date: Date = new Date()): string {
  const { year, month } = datePartsIn(date, tz);
  return `${year}-${pad2(month)}`;
}

/** "HH:MM" of an epoch-seconds timestamp on the wall clock of `tz`. */
export function fmtTimeIn(ts: number, tz?: string | null): string {
  const opts: Intl.DateTimeFormatOptions = { hour: "2-digit", minute: "2-digit", hourCycle: "h23" };
  if (tz && isValidTimeZone(tz)) opts.timeZone = tz;
  return new Date(ts * 1000).toLocaleTimeString([], opts);
}
